/**
 * §P.1 fase 2 · El pasaporte del alumno, para imprimirlo en el mostrador.
 *
 * ## Es el mismo que ve la familia, pero aquí porque alguien lo pide en persona
 *
 * «Para cuando quien lo pide está delante del mostrador», dice el contrato. Y dice
 * también lo que de verdad importa de este documento: **es del alumno, no del club**
 * — una familia que se muda se lo lleva—. Por eso el encabezado lleva el nombre del
 * alumno primero y el del club debajo, y no al revés.
 *
 * ## Imprimir de verdad, con `window.print()`
 *
 * No hay PDF desde el panel: el PDF con la marca del club existe en la app de
 * familias (§F). Aquí lo que hace falta es una hoja que salga bien de la impresora
 * del club, así que hay una regla `@media print` que quita la cabecera, el menú y los
 * botones. Generar un PDF en el cliente para esto sería tres librerías y una hoja
 * peor.
 *
 * ## «Que exista es, en sí, retención»
 *
 * Está en el contrato y explica por qué esta pantalla merece cuidado: cuatro años de
 * historia no se llevan en una carpeta, y enseñarlos impresos el día que una familia
 * duda es más elocuente que cualquier argumento.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';

interface Pasaporte {
  readonly student_name: string;
  readonly academy_name: string;
  readonly joined_on: Date | string;
  readonly current_rank: string | null;
  readonly current_stripes: number;
  readonly ranks: readonly {
    rank_name: string;
    color_hex: string;
    awarded_on: Date | string;
    certificate_ref: string | null;
  }[];
  readonly exams: readonly {
    name: string;
    scheduled_at: Date | string;
    result: string | null;
    rank_name: string | null;
  }[];
  readonly competitions: readonly {
    name: string;
    starts_on: Date | string;
    category_name: string | null;
    placement: number | null;
    medal: string | null;
  }[];
  readonly medals: { gold: number; silver: number; bronze: number };
  readonly attendance_rate: number | null;
}

export function PasaporteImprimible({
  club,
  alumnoId,
}: {
  club: string;
  alumnoId: string;
}) {
  const pasaporte = useQuery({
    queryKey: ['pasaporte-imprimible', club, alumnoId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .passport.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Pasaporte;
    },
  });

  if (pasaporte.isPending) {
    return <Cargando filas={3} etiqueta="Cargando el pasaporte" />;
  }

  if (pasaporte.isError || !pasaporte.data) {
    return (
      <PanelError
        problema={aProblema(pasaporte.error)}
        reintentar={() => void pasaporte.refetch()}
      />
    );
  }

  const p = pasaporte.data;
  const medallas = p.medals.gold + p.medals.silver + p.medals.bronze;

  return (
    /* `imprimible` es lo que la hoja de impresión deja en pie. Ver `tema.css`. */
    <div className="imprimible">
      <div
        className="tutor-acciones sin-imprimir"
        style={{ marginBlockStart: 0 }}
      >
        <button type="button" onClick={() => window.print()}>
          Imprimirlo
        </button>
      </div>

      <p className="tenue sin-imprimir">
        El mismo que ve la familia en su app. <strong>Es del alumno</strong>: si
        se muda de club, se lo lleva.
      </p>

      <article className="pasaporte-hoja">
        {/* El alumno primero y el club debajo: el documento es suyo. */}
        <header>
          <h3>{p.student_name}</h3>
          <p className="tenue">
            {p.academy_name} · desde el {fecha(p.joined_on)}
          </p>
        </header>

        <dl className="datos">
          <div>
            <dt>Grado actual</dt>
            <dd>
              {p.current_rank ?? 'Sin grado todavía'}
              {p.current_stripes > 0
                ? ` · ${p.current_stripes} ${p.current_stripes === 1 ? 'franja' : 'franjas'}`
                : ''}
            </dd>
          </div>
          <div>
            <dt>Grados conseguidos</dt>
            <dd>{p.ranks.length}</dd>
          </div>
          <div>
            <dt>Medallas</dt>
            {/* Sin desglosar cuando no hay ninguna: «0 oro, 0 plata» es ruido. */}
            <dd>
              {medallas === 0
                ? 'Ninguna todavía'
                : `${p.medals.gold} oro · ${p.medals.silver} plata · ${p.medals.bronze} bronce`}
            </dd>
          </div>
          <div>
            <dt>Asistencia</dt>
            <dd>
              {p.attendance_rate === null
                ? 'Sin datos'
                : `${Math.round(p.attendance_rate * 100)} %`}
            </dd>
          </div>
        </dl>

        {p.ranks.length > 0 && (
          <>
            <h4>Los grados, uno a uno</h4>
            <ul className="pasaporte-lista">
              {p.ranks.map((r, i) => (
                <li key={`${r.rank_name}-${i}`}>
                  {/*
                    El color del cinturón es **dato de dominio** y no se tiñe con la
                    marca: el verde del cinturón verde es verde en todos los clubes
                    del mundo.
                  */}
                  <span
                    className="cinturon-punto"
                    style={{ background: r.color_hex }}
                    aria-hidden="true"
                  />
                  <span>{r.rank_name}</span>
                  <span className="tenue">{fecha(r.awarded_on)}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {p.exams.length > 0 && (
          <>
            <h4>Exámenes</h4>
            <ul className="pasaporte-lista">
              {p.exams.map((e, i) => (
                <li key={`${e.name}-${i}`}>
                  <span>{e.name}</span>
                  <span className="tenue">
                    {fecha(e.scheduled_at)}
                    {e.result ? ` · ${e.result}` : ''}
                    {e.rank_name ? ` · ${e.rank_name}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}

        {p.competitions.length > 0 && (
          <>
            <h4>Competiciones</h4>
            <ul className="pasaporte-lista">
              {p.competitions.map((c, i) => (
                <li key={`${c.name}-${i}`}>
                  <span>{c.name}</span>
                  <span className="tenue">
                    {fecha(c.starts_on)}
                    {c.category_name ? ` · ${c.category_name}` : ''}
                    {c.medal
                      ? ` · ${c.medal}`
                      : c.placement
                        ? ` · ${c.placement}.º`
                        : ''}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}

        {p.ranks.length === 0 &&
          p.exams.length === 0 &&
          p.competitions.length === 0 && (
            <p className="tenue">
              Todavía no hay historia que imprimir. Se va llenando con cada
              grado y cada competición, y es lo que dentro de cuatro años hará
              que este papel valga algo.
            </p>
          )}
      </article>
    </div>
  );
}
